const _ = require('lodash');
const combinators = require('./lib/combinators');
const validators = require('./lib/validators');
const { ValidatorTypes, FormatterTypes } = require('./lib/constants');

/**
 * @module firstmac-validation-lib
 * @description This is the main entry point for the library
 */

/**
 * Returns a curried combinator function
 * @param {string} combinatorName The name of the combinator
 * @param {object} context The context to pass to the combinator
 * @param {boolean} detailedError If set to true and combinator supports
 * detailed errors, combinator will return an error object that
 * includes the context rather than just the error slug
 * @return {function(*): *}
 */
function curryCombinator(combinatorName, context, detailedError) {
  return (value) => combinators[combinatorName](value, context, detailedError);
}

/**
 * Performs validation on a value
 * @param {string|ValidatorType} slug The slug of the validator to use
 * @param {any} value The value to validate
 * @param {object} context The context to pass to the validator
 * @param {boolean} detailedError If set to true and combinator supports
 * detailed errors, combinator will return an error object that
 * includes the context rather than just the error slug
 * @return {Promise<string[]>} A promise that resolves to an array of error messages
 */
async function validate(slug, value, context, detailedError = false) {
  if (!_.has(validators, slug)) {
    throw new Error('validator-not-found');
  }
  // assign to be merged in with the validator context
  const incomingContext = context;
  // load the validators and combinators
  const loadedCombinators = _.map(validators[slug], (validator) => {
    // check if the combinatorDef bundles a context
    if (_.isArray(validator)) {
      const [name, context] = validator;
      // merge the context for each instance so that any extra incoming context can be applied
      const thisContext = _.merge(_.cloneDeep(context), incomingContext || {});
      // merge in this context with any custom coming through
      return curryCombinator(name, thisContext, detailedError);
    }

    // check if the combinatorDef is a string
    if (_.isString(validator)) {
      return combinators[validator];
    }

    return null;
  });
  const validatorPromises = _.map(loadedCombinators, async (lc) => {
    if (lc == null) {
      throw new Error('combinator-not-found');
    }

    return await lc(value, context || { });
  });

  const validatorResults = await Promise.all(validatorPromises);
  return _.filter(validatorResults, (result) => result !== null);
}

/**
 * Checks if a value is valid
 * @param {string} slug The slug of the validator to use
 * @param {any} value The value to validate
 * @param {object} context The context to pass to the validator
 * @return {Promise<boolean>} A promise that resolves to true if the value is valid, otherwise false
 */
async function isValid(slug, value, context) {
  const results = await validate(slug, value, context);
  return results.length === 0;
}

/**
 * Validates data using a schema defined in firstmac-validation-lib.
 *
 * This function runs the specified validator against the provided data and
 * returns one of the following:
 *
 *  - `null` when validation passes (no errors)
 *  - an array of error messages when validation fails
 *  - a string error message when the validator returns a single error
 *
 * The caller is responsible for interpreting the returned value and converting
 * it into HTTP errors or other application-specific responses.
 *
 * @param {string} validator
 *   The name of the validator to execute.
 *
 * @param {*} data
 *   The input data to validate.
 *
 * @param {Object} [opt={}]
 *   Optional validation options, including formatter overrides.
 *
 * @returns {Promise<null|string|string[]>}
 *   Returns `null` if valid, otherwise returns validation errors.
 */
const validateSchema = async (validator, data, opt = {}) => {
  const options = {
    formatter: FormatterTypes.SCHEMA_NAME_ARGUMENT,
    ...opt,
  };

  const result = await validate(validator, data, options);

  // Valid → return null
  if (!result || result.length === 0) {
    return null;
  }

  // Invalid → return result array or string
  return result;
};

module.exports = {
  validate,
  isValid,
  Constants: {
    FormatterTypes,
    ValidatorTypes,
  },
  validateSchema,
};
